import { Injectable } from '@angular/core';
import { EmailGroup } from '../../model/admin/email-group';
import { EmailGroupInput } from '../../model/admin/email-group-input';
import { EmailGroupMember } from '../../model/admin/email-group-member';
import { EmailRequest } from '../../model/admin/email-request';
import { DirtyStatus } from '../../model/common/dirty-status';
import { Contact } from '../../model/contacts/contact';
import { CompanyDataTableInput } from '../../model/admin/company-data-table-input';
import { MOCEventCatInput } from '../../model/admin/moc-event-cat-input';
import { NoteTypeInput } from '../../model/admin/note-type-input';
import { MonLogTypeInput } from '../../model/admin/mon-log-type-input';
import { UpdateRoleInput } from '../../model/admin/update-role-input';
import { Claim, UpdateClaimInput } from '../../model/admin/claim';
import { BusinessUnit } from 'src/app/model/admin/business-unit';
import { JobRoleDataTableInput } from '../../model/admin/job-role-data-table-input';
import { isNullOrUndefined } from '../../utils/utils';
import { Role, RoleConnection, RoleSearch } from '../../model/admin/role';
import { Company, CompanyConnection } from 'src/app/model/contacts/company';
import { AOR } from 'src/app/model/locations/aor';

@Injectable({
  providedIn: 'root',
})
export class AdminBuilderService {
  constructor() {}

  buildEmailGroupInput(
    emailGroup: EmailGroup,
    targetBusinessUnits: BusinessUnit[]
  ): EmailGroupInput | null {
    const assignedUnits: BusinessUnit[] = !isNullOrUndefined(
      targetBusinessUnits
    )
      ? targetBusinessUnits
      : [];
    if (!isNullOrUndefined(emailGroup)) {
      if (emailGroup.dirtyStatus === DirtyStatus.NEW) {
        return {
          name: emailGroup.name,
          enabled: emailGroup.enabled,
          EmailGroupMembers: [],
          expectedVersion: 0,
          businessUnits: assignedUnits.map((m) => ({
            id: m.id,
            name: m.name,
          })),
        };
      } else {
        const members: EmailGroupMember[] = !isNullOrUndefined(
          emailGroup.EmailGroupMembers
        )
          ? emailGroup.EmailGroupMembers
          : [];
        return {
          name: emailGroup.name,
          enabled: emailGroup.enabled,
          EmailGroupMembers: members.map((m) => ({
            emailGroupId: emailGroup.id,
            address: m.address,
          })),
          expectedVersion: emailGroup.version!,
          businessUnits: assignedUnits.map((m) => ({
            id: m.id,
            name: m.name,
          })),
        };
      }
    } else {
      return null;
    }
  }

  buildEmailGroupContact(contact: Contact): any {
    if (!isNullOrUndefined(contact)) {
      return {
        fullName: contact.fullName,
        address: contact.adAccountName,
      };
    }
  }

  buildNewEmailGroupMember(address: string): EmailGroupMember {
    const retVal: EmailGroupMember = {
      id: '',
      address: address,
      dirtyStatus: DirtyStatus.NEW,
      version: 0,
    };
    return retVal;
  }

  buildEmailRequest(
    id: any,
    toList: string,
    bccList: string,
    emailNotes: string
  ): EmailRequest {
    return {
      id: id,
      toList: toList,
      bccList: bccList,
      emailNotes: emailNotes,
    };
  }

  buildJobRoleDataTableInput(
    input: any,
    isNew: boolean,
    shiftGroups: string[]
  ): JobRoleDataTableInput | null {
    if (!isNullOrUndefined(input)) {
      return {
        name: input.name,
        shiftGroups: shiftGroups,
        enabled: input.enabled,
        expectedVersion: isNew ? 0 : input.version,
      };
    } else {
      return null;
    }
  }

  buildCompanyDataTableInput(
    input: any,
    isNew: boolean
  ): CompanyDataTableInput | null {
    if (!isNullOrUndefined(input)) {
      return {
        name: input.name,
        abbreviation: input.abbreviation,
        enabled: input.enabled,
        expectedVersion: isNew ? 0 : input.version,
        dirtyStatus: input.dirtyStatus,
      };
    } else {
      return null;
    }
  }

  buildMOCEventCatInput(
    input: any,
    isNew: boolean,
    targetBusinessUnits: BusinessUnit[]
  ): MOCEventCatInput | null {
    if (!input) {
      return null;
    }

    const assignedUnits: BusinessUnit[] = !isNullOrUndefined(
      targetBusinessUnits
    )
      ? targetBusinessUnits
      : [];
    return {
      name: input.name,
      // shiftGroupId: isNew ? input.shiftGroup : input.ShiftGroup.id,
      enabled: input.enabled,
      expectedVersion: isNew ? 0 : input.version,
      dirtyStatus: input.dirtyStatus,
      businessUnits: assignedUnits.map((m) => ({
        id: m.id,
        name: m.name,
      })),
    };
  }

  buildNoteTypeInput(
    input: any,
    isNew: boolean,
    targetAORs: AOR[]
  ): NoteTypeInput | null {
    if (!input) {
      return null;
    }

    const assignedAORs: AOR[] = !isNullOrUndefined(targetAORs)
      ? targetAORs
      : [];
    return {
      name: input.name,
      enabled: input.active,
      expectedVersion: isNew ? 0 : input.version,
      dirtyStatus: input.dirtyStatus,
      categories: input.categories,
      createdBy: input.createdBy,
      updatedBy: input.updatedBy,
      aors: assignedAORs.map((m) => ({
        id: m.id,
        name: m.name,
      })),
    };
  }

  buildMonLogTypeInput(
    input: any,
    isNew: boolean,
    targetBusinessUnits: BusinessUnit[]
  ): MonLogTypeInput | null {
    if (!isNullOrUndefined(input)) {
      const assignedUnits: BusinessUnit[] = !isNullOrUndefined(
        targetBusinessUnits
      )
        ? targetBusinessUnits
        : [];
      return {
        name: input.name,
        enabled: input.enabled,
        expectedVersion: isNew ? 0 : input.version,
        dirtyStatus: input.dirtyStatus,
        businessUnits: assignedUnits.map((m) => ({
          id: m.id,
          name: m.name,
        })),
        requiresCRER: input.requiresCRER,
      };
    } else {
      return null;
    }
  }

  buildRoleClaimInput(
    input: any,
    targetClaims: Claim[]
  ): UpdateRoleInput | null {
    if (!isNullOrUndefined(input)) {
      const members: Claim[] = !isNullOrUndefined(targetClaims)
        ? targetClaims
        : [];
      return {
        id: input.id,
        claims: members.map((m) => ({
          id: m.id,
          name: m.name,
        })),
        businessUnits: null,
      };
    } else {
      return null;
    }
  }

  buildClaimRoleInput(
    input: any,
    targetRoles: Role[]
  ): UpdateClaimInput | null {
    if (!isNullOrUndefined(input)) {
      const roles: Role[] = !isNullOrUndefined(targetRoles) ? targetRoles : [];
      return {
        id: input.id,
        roles: roles.map((m) => ({
          id: m.id,
          name: m.name,
        })),
      };
    } else {
      return null;
    }
  }

  buildRoleBusinessUnitsInput(
    input: any,
    targetBusinessUnits: BusinessUnit[]
  ): UpdateRoleInput | null {
    if (!isNullOrUndefined(input)) {
      const assignedUnits: BusinessUnit[] = !isNullOrUndefined(
        targetBusinessUnits
      )
        ? targetBusinessUnits
        : [];
      return {
        id: input.id,
        claims: null,
        businessUnits: assignedUnits.map((m) => ({
          id: m.id,
          name: m.name,
        })),
      };
    } else {
      return null;
    }
  }

  buildCompany(item: any): Company {
    return {
      id: item.id,
      name: item.name,
      abbreviation: item.abbreviation,
      enabled: item.enabled,
      version: item.version,
    };
  }

  buildCompanyConnection(item: any): CompanyConnection {
    return {
      items: item.items.map((i: any) => this.buildCompany(i)),
      totalRecords: item.totalRecords,
    };
  }

  buildBusinessUnit(item: any): BusinessUnit {
    return {
      id: item.id,
      name: item.name,
    };
  }

  buildClaim(item: any): Claim {
    return {
      id: item.id,
      name: item.name,
    };
  }

  buildRole(item: any): Role {
    return {
      id: item.id,
      name: item.name,
      businessUnits: item.businessUnits.map((i: any) =>
        this.buildBusinessUnit(i)
      ),
      claims: item.claims.map((i: any) => this.buildClaim(i)),
    };
  }

  buildRoleSearch(item: any): RoleSearch {
    return {
      id: item.id,
      name: item.name,
      claims: item.claims,
      businessUnits: item.businessUnits,
    };
  }

  buildRoleConnection(item: any): RoleConnection {
    return {
      items: item.items.map((i: any) => this.buildRoleSearch(i)),
      totalRecords: item.totalRecords,
    };
  }

  buildEmailGroupMember(item: any): EmailGroupMember {
    return {
      id: item.id,
      address: item.address,
      version: item.version,
    };
  }

  buildEmailGroup(item: any): EmailGroup {
    return {
      id: item.id,
      name: item.name,
      enabled: item.enabled,
      EmailGroupMembers: item.EmailGroupMembers.map((i: any) =>
        this.buildEmailGroupMember(i)
      ),
      businessUnits: item.businessUnits,
      createdBy: item.creatdBy,
      updater: item.updater,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      version: item.version,
      businessUnitString:
        item.businessUnits?.map((i: any) => i.name).join(', ') ?? '',
    };
  }
}
